import { useCallback, useRef, useEffect } from 'react';

import { Order } from '../../data-access/types/orders';

import { WEEK_WIDTH, getWeekOffset } from './utils';

interface UseTimelineScrollProps {
  minDate: Date;
  currentDateOffset: number; // This is actually week offset now, but keeping name for backward compatibility
}

export function useTimelineScroll({
  minDate,
  currentDateOffset,
}: UseTimelineScrollProps) {
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const leftScrollRef = useRef<HTMLTableSectionElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);

  const handleLeftTableScroll = useCallback(
    (e: React.UIEvent<HTMLTableSectionElement>) => {
      const scrollTop = e.currentTarget.scrollTop;

      if (rightScrollRef.current) {
        rightScrollRef.current.scrollTop = scrollTop;
      }
    },
    [],
  );

  const handleVerticalScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const scrollTop = e.currentTarget.scrollTop;
      const scrollLeft = e.currentTarget.scrollLeft;

      if (
        headerScrollRef.current &&
        Math.abs(headerScrollRef.current.scrollLeft - scrollLeft) > 1
      ) {
        headerScrollRef.current.scrollTo({
          left: scrollLeft,
          behavior: 'auto',
        });
      }

      if (leftScrollRef.current) {
        leftScrollRef.current.scrollTop = scrollTop;
      }
    },
    [],
  );

  const scrollToOrder = useCallback(
    (order: Order) => {
      if (!rightScrollRef.current || !headerScrollRef.current) {
        return;
      }

      // Get timelines from the first analysis command
      const analysisCommand = order.analysis_commands[0];
      if (!analysisCommand || !analysisCommand.timelines.length) {
        return;
      }

      const orderDates = analysisCommand.timelines.flatMap((timeline) => [
        new Date(timeline.start_date),
        new Date(timeline.end_date),
      ]);
      const earliestOrderDate = new Date(
        Math.min(...orderDates.map((date) => date.getTime())),
      );

      const weekOffset = getWeekOffset(earliestOrderDate, minDate);
      const scrollPosition = Math.max(0, weekOffset * WEEK_WIDTH - 100);

      rightScrollRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });

      headerScrollRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    },
    [minDate],
  );

  // Auto-scroll to current date on mount
  useEffect(() => {
    if (
      rightScrollRef.current &&
      headerScrollRef.current &&
      currentDateOffset >= 0
    ) {
      const scrollPosition = Math.max(0, currentDateOffset * WEEK_WIDTH - 200);
      setTimeout(() => {
        rightScrollRef.current?.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
        headerScrollRef.current?.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [currentDateOffset]);

  return {
    headerScrollRef,
    bodyScrollRef,
    leftScrollRef,
    rightScrollRef,
    handleLeftTableScroll,
    handleVerticalScroll,
    scrollToOrder,
  };
}
